/**
 * RollbackModal
 *
 * Confirmation dialog for rolling back to a previous version.
 * Requires a rollback note before proceeding.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Textarea } from '../../../ui/textarea';
import { Label } from '../../../ui/label';

interface RollbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionNumber: string;
  type: 'taxonomy' | 'prompt';
  onConfirm: (note: string) => void;
  isRollingBack?: boolean;
}

const RollbackModal: React.FC<RollbackModalProps> = ({
  open,
  onOpenChange,
  versionNumber,
  type,
  onConfirm,
  isRollingBack = false,
}) => {
  const [note, setNote] = useState('');

  const handleConfirm = () => {
    if (note.trim()) {
      onConfirm(note.trim());
      setNote('');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setNote('');
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Rollback to v{versionNumber}</DialogTitle>
          <DialogDescription>
            This will restore v{versionNumber} as the live {type} version. The
            current live version will be archived. This action takes effect
            immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label htmlFor="rollback-note" className="text-sm font-medium">
            Rollback Reason <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="rollback-note"
            placeholder="Why are you rolling back to this version?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-2"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            This note will be recorded in the audit trail.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isRollingBack}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!note.trim() || isRollingBack}
          >
            {isRollingBack ? 'Rolling back...' : 'Rollback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RollbackModal;
